/**
 * ProviderDeviceFleet — the hardware provider's device list: every unit built
 * on the manufacturer's boards, which software provider each is connected to,
 * deployed state, live status, and last seen. Filterable by deployed state
 * and software provider.
 *
 * Used in two places:
 *   - the seller's own Devices page (left nav) — names only, no cross-links
 *   - the super-admin Devices tab on HardwareProviderDetail — linkThrough
 *     renders device + software-provider links.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { getManufacturerDevices } from '../services/manufacturers';
import { formatWhen } from './BoardPriceModal';
import {
  TableContainer,
  StyledTable,
  Th,
  Td,
  StatusChip,
  LoadingText,
  EmptyState,
  MutedText,
  Caption,
  Row,
  Stack,
  Select,
} from './styled';

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin: ${({ theme }) => theme.spacing(6)} 0 ${({ theme }) => theme.spacing(3)};
`;

const ProviderLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.font.weight.medium};

  &:hover { text-decoration: underline; }
`;

function statusVariant(status: string): 'positive' | 'critical' | 'neutral' {
  if (status === 'online') return 'positive';
  if (status === 'offline') return 'critical';
  return 'neutral';
}

export function ProviderDeviceFleet({ manufacturerId, manufacturerName, linkThrough }: {
  manufacturerId: number;
  manufacturerName: string;
  linkThrough: boolean;
}) {
  const [deployedFilter, setDeployedFilter] = useState<'all' | 'deployed' | 'undeployed'>('deployed');
  const [providerFilter, setProviderFilter] = useState<string>('');

  const deviceOptions = {
    deployed: deployedFilter === 'all' ? undefined : deployedFilter === 'deployed',
    companyId: providerFilter || undefined,
    limit: 200,
  };
  const { data: deviceData, isLoading: devicesLoading } = useQuery({
    queryKey: ['manufacturer-devices', manufacturerId, deviceOptions],
    queryFn: () => getManufacturerDevices(manufacturerId, deviceOptions),
    enabled: Number.isFinite(manufacturerId),
  });

  const devices = deviceData?.devices ?? [];
  const providers = deviceData?.software_providers ?? [];

  return (
    <>
      <SectionHeader>
        <MutedText>
          Devices built on {manufacturerName} boards and the software provider each is connected to.
        </MutedText>
        <Row gap={2}>
          <Select value={deployedFilter} onChange={(e) => setDeployedFilter(e.target.value as typeof deployedFilter)}>
            <option value="deployed">Deployed</option>
            <option value="undeployed">Not deployed</option>
            <option value="all">All</option>
          </Select>
          <Select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
            <option value="">All software providers</option>
            {providers.map((p) => (
              <option key={String(p.company_id)} value={String(p.company_id)}>{p.company_name}</option>
            ))}
          </Select>
        </Row>
      </SectionHeader>

      {devicesLoading ? (
        <LoadingText>Loading devices...</LoadingText>
      ) : devices.length === 0 ? (
        <EmptyState><p>No devices match.</p></EmptyState>
      ) : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>Device</Th>
                <Th>Board</Th>
                <Th>Software provider</Th>
                <Th>Deployed</Th>
                <Th>Status</Th>
                <Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.id}>
                  <Td>
                    <Stack gap={1}>
                      {linkThrough
                        ? <ProviderLink to={`/devices/${d.id}`}>{d.name || d.device_id}</ProviderLink>
                        : <strong>{d.name || d.device_id}</strong>}
                      {d.name && <Caption>{d.device_id}</Caption>}
                    </Stack>
                  </Td>
                  <Td>
                    {d.board_display_name || d.board}
                    {d.hardware_version && <Caption> {d.hardware_version}</Caption>}
                  </Td>
                  <Td>
                    {d.company_name
                      ? (linkThrough
                          ? <ProviderLink to={`/software-providers/${d.company_id}`}>{d.company_name}</ProviderLink>
                          : d.company_name)
                      : <MutedText>unassigned</MutedText>}
                  </Td>
                  <Td>
                    {d.deployed
                      ? <Caption>{d.deployed_at ? formatWhen(d.deployed_at) : 'yes'}</Caption>
                      : <StatusChip variant="neutral">no</StatusChip>}
                  </Td>
                  <Td><StatusChip variant={statusVariant(d.status)}>{d.status}</StatusChip></Td>
                  <Td><Caption>{d.last_seen ? formatWhen(d.last_seen) : '—'}</Caption></Td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}
      {deviceData && deviceData.total > devices.length && (
        <Caption>Showing {devices.length} of {deviceData.total}.</Caption>
      )}
    </>
  );
}
